import { useEffect, useMemo, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import toast from 'react-hot-toast';

import { qsParse } from 'helpers/queryString';
import { getCompanyColumns } from 'pages/listDetail/getCompanyColumns';
import { useCompanyTableFunctions } from 'pages/listDetail/hooks/useCompanyTableFunctions';
import { useTableRows } from 'pages/listDetail/hooks/useCompanyTableRows';
import { useRemoveContactFromSharedListMutation, useUpdateSharedListMutation } from 'services/contacts';
import { companyDefaultFilters } from 'components/organisms/companyFilters/defaultFilters';
import { parseFiltersData, reverseParsedFilters } from '../../../services/contacts/filters';
import { useActionItems } from './useActionItems';
import { useFetchCompanies } from './useFetchCompanies';
import { useFlattenCompanyList } from './useFlattenCompanyList';

export const DEFAULT_SORT = {
  sortBy: 'contactsCount',
  sortDirection: 'desc',
};

export const useCompanyList = (sharedList) => {
  const location = useLocation();
  const [updateSharedList, { isLoading: isSavingFilters }] = useUpdateSharedListMutation();
  const [removeContactFromSharedList, { isLoading: isLoadingRemoveContact }] = useRemoveContactFromSharedListMutation();
  const {
    data,
    isLoading,
    isFetching,
    activeNetwork,
    listId,
    queryParams,
    setQueryParams,
    filtersCount,
    onSetFilters,
  } = useFetchCompanies({ defaultSort: DEFAULT_SORT, defaultFilters: companyDefaultFilters });

  const companies = useFlattenCompanyList(data);

  const companiesColumns = getCompanyColumns(new Set([]));
  const currentPage = Math.ceil(data?.ids?.length / 10);
  const hasMore = currentPage + 1 <= Math.ceil(data?.meta?.queryTotal / 10);

  useEffect(() => {
    if (sharedList?.smartSharedList) {
      onSetFilters(reverseParsedFilters(sharedList.smartSharedList.filters));
    }
  }, [sharedList?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const onSaveFilters = useCallback(
    async (filters) => {
      const payload = {
        smartListOptions: {
          filters: parseFiltersData(filters),
          enabled: true,
        },
      };

      await updateSharedList({
        listId: sharedList.id,
        ...payload,
      });

      toast('Filters saved', { icon: 'success', id: 'filters-saved' });
    },
    [updateSharedList, sharedList.id],
  );

  const onSort = (args) => {
    const [{ desc, column }] = args;

    setQueryParams({ ...queryParams, sortBy: column.accessor, sortDirection: desc ? 'desc' : 'asc', page: 1 });
  };

  const removeSharedListDomains = useCallback(
    (domainToRemove, company) => {
      updateSharedList({
        listId: sharedList.id,
        domains: sharedList.domains.filter((domain) => domain !== domainToRemove),
        filters: queryParams.filters,
        action: 'remove-domain',
        companyId: company.id,
      });
      toast('Company removed from this list', { icon: 'success', id: 'company-removed-from-list' });
    },
    [updateSharedList, sharedList?.id, sharedList.domains, queryParams.filters],
  );

  const actionMenuItems = useActionItems({
    removeSharedListDomains,
  });
  const { handleSort, onColumnAction } = useCompanyTableFunctions({ companies: data?.entities, onSort });
  const tableRows = useTableRows({
    network: activeNetwork,
    sharedList,
    companies,
    queryParams,
  });

  const onLoadMore = () => {
    setQueryParams({
      ...queryParams,
      page: currentPage + 1,
    });
  };

  const initialSortBy = useMemo(() => {
    const params = qsParse(location);
    let id = DEFAULT_SORT.sortBy;
    let desc = DEFAULT_SORT.sortDirection === 'desc';
    if (params.sortBy) {
      id = params.sortBy;
    }
    if (params.sortDirection) {
      desc = params.sortDirection === 'desc';
    }
    return { id, desc };
  }, [location]);

  return {
    activeNetwork,
    listId,
    companiesColumns,
    tableRows,
    totalItems: data?.meta?.queryTotal,
    isFetching,
    isLoading,
    handleSort,
    actionMenuItems,
    onColumnAction,
    queryParams,
    filtersCount,
    onSetFilters,
    onSaveFilters,
    removeContactFromSharedList,
    isLoadingRemoveContact,
    hasMore,
    onLoadMore,
    savedFilters: sharedList?.smartSharedList?.filters,
    isSavingFilters,
    initialSortBy,
  };
};
